// Generated by scripts/sportsbook_history/generate.mjs
import React from "react";
import PropTypes from "prop-types";

import useServiceContextHook from "../../../_hooks/service.context.hook";
import { useCasinoBettingHistoryQuery } from "../query/useCasinoBettingHistory.query";

const TAB_EMPTY_LABEL = Object.freeze({
  Entire: "베팅내역이 없습니다.",
  Slots: "슬롯 베팅내역이 없습니다.",
  "Live Casino": "라이브 카지노 베팅내역이 없습니다.",
  Sports: "외부 스포츠 베팅내역이 없습니다.",
  Minigames: "미니게임 베팅내역이 없습니다.",
});

const formatMoney = (value) => {
  const amount = +(value ?? 0);
  return Number.isFinite(amount) ? Math.round(amount).toLocaleString("ko-KR") : "0";
};

const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getResultMeta = (row) => {
  const resultType = String(row?.bet?.result_transaction_type || "").toUpperCase();
  if (resultType === "WIN") return { key: "won", label: "당첨" };
  if (["LOSE", "LOSS", "LOST"].includes(resultType)) return { key: "lost", label: "미당첨" };
  if (["CANCEL", "CANCELLED", "REFUND", "VOID"].includes(resultType)) {
    return { key: "void", label: "취소/환급" };
  }
  return { key: "pending", label: "확인중" };
};

const getHistoryRowKey = (row) =>
  row?.id ||
  row?.bet_id ||
  row?.bet?.id ||
  row?.bet?.transaction_id ||
  [
    row?.bet?.transaction_timestamp || row?.bet?.created_at || row?.created_at || "unknown-time",
    row?.bet?.title || row?.game_name || "unknown-game",
    row?.amount ?? "unknown-amount",
  ].join("::");

const CasinoBettingHistoryDisplay = ({
  tab,
  dateRange,
  customStartDate,
  customEndDate,
}) => {
  const {
    services: { GamesService },
  } = useServiceContextHook();
  const query = useCasinoBettingHistoryQuery({
    service: GamesService,
    tab,
    dateRange,
    customStartDate,
    customEndDate,
  });
  const pageStart = query.totalCount === 0 ? 0 : (query.page - 1) * query.pageLimit + 1;
  const pageEnd = Math.min(query.totalCount, query.page * query.pageLimit);

  if (query.loading && query.rows.length === 0) {
    return (
      <div className="crypto-game-history-loading" aria-label="베팅내역 불러오는 중">
        {Array.from({ length: 5 }, (_, index) => (
          <span key={index} />
        ))}
      </div>
    );
  }

  return (
    <section className="crypto-game-history" aria-label="카지노 및 게임 베팅내역">
      <div className="crypto-game-history-meta" aria-live="polite">
        {query.error || `${pageStart.toLocaleString()}-${pageEnd.toLocaleString()} / ${query.totalCount.toLocaleString()}건`}
      </div>

      {query.rows.length === 0 ? (
        <div className={`crypto-game-history-empty${query.error ? " is-error" : ""}`}>
          {query.error || TAB_EMPTY_LABEL[tab] || TAB_EMPTY_LABEL.Entire}
        </div>
      ) : (
        <div className="crypto-game-history-table-wrap">
          <table className="crypto-game-history-table">
            <thead>
              <tr>
                <th>번호</th>
                <th>게임</th>
                <th>베팅금</th>
                <th>당첨금</th>
                <th>결과</th>
                <th>베팅시간</th>
              </tr>
            </thead>
            <tbody>
              {query.rows.map((row, index) => {
                const result = getResultMeta(row);
                const winAmount = result.key === "won" ? row?.bet?.result_amount : 0;
                return (
                  <tr key={getHistoryRowKey(row)}>
                    <td data-label="번호">{(query.page - 1) * query.pageLimit + index + 1}</td>
                    <td data-label="게임" className="crypto-game-history-title">
                      {row?.bet?.title || row?.game_name || "게임"}
                    </td>
                    <td data-label="베팅금">{formatMoney(row?.amount)}원</td>
                    <td data-label="당첨금" className={result.key === "won" ? "is-won" : ""}>
                      {formatMoney(winAmount)}원
                    </td>
                    <td data-label="결과">
                      <span className={`crypto-game-history-result is-${result.key}`}>{result.label}</span>
                    </td>
                    <td data-label="베팅시간">
                      {formatTime(row?.bet?.transaction_timestamp || row?.bet?.created_at || row?.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {query.totalPages > 1 ? (
        <div className="crypto-game-history-pagination">
          <button
            type="button"
            disabled={query.page <= 1 || query.loading}
            onClick={() => query.setPage((current) => Math.max(1, current - 1))}
          >
            이전
          </button>
          <span>{query.page} / {query.totalPages}</span>
          <button
            type="button"
            disabled={query.page >= query.totalPages || query.loading}
            onClick={() => query.setPage((current) => Math.min(query.totalPages, current + 1))}
          >
            다음
          </button>
        </div>
      ) : null}
    </section>
  );
};

CasinoBettingHistoryDisplay.propTypes = {
  tab: PropTypes.string.isRequired,
  dateRange: PropTypes.string.isRequired,
  customStartDate: PropTypes.string,
  customEndDate: PropTypes.string,
};

CasinoBettingHistoryDisplay.defaultProps = {
  customStartDate: "",
  customEndDate: "",
};

export default CasinoBettingHistoryDisplay;
